import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { Message } from '../types';
import { format, parseISO, isValid } from 'date-fns';
import EmailsList from './emailsList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseButton } from 'components/BaseButton';

interface TableMessageProps {
  t: TFunction;
  messages: Message[];
  setSelectedMessage: React.Dispatch<React.SetStateAction<Message | undefined>>;
}

const COARSE_POINTER = '@media (hover: none) and (pointer: coarse)';

const useStyles = makeStyles((theme: any) =>
  createStyles({
    tableContainer: {
      marginTop: theme.spacing(3),
    },
    table: {
      tableLayout: 'fixed',
    },
    headerCell: {
      color: theme.palette.primary.darker,
      fontWeight: 500,
      borderBottomColor: theme.palette.input.border,
    },
    cellContainer: {
      borderBottomColor: theme.palette.input.border,
      color: theme.palette.primary.dark,
      verticalAlign: 'top',
    },
    clampOneLine: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    clampTwoLines: {
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      overflowWrap: 'anywhere',
    },
    recipients: {
      minWidth: 0,
    },
    recipientsMeta: {
      display: 'block',
      color: theme.palette.text.light,
      marginTop: theme.spacing(0.5),
    },
    rowAction: {
      [COARSE_POINTER]: {
        minWidth: theme.spacing(5.5),
        minHeight: theme.spacing(5.5),
      },
    },
    cardList: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2),
    },
    card: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.5),
      minWidth: 0,
      border: `1px solid ${theme.palette.border.line}`,
    },
    cardHeader: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: theme.spacing(1),
    },
    subject: {
      fontWeight: 500,
      color: theme.palette.primary.main,
      overflowWrap: 'anywhere',
      minWidth: 0,
    },
    status: {
      color: theme.palette.primary.main,
      fontWeight: 500,
      textTransform: 'capitalize',
    },
    fields: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: theme.spacing(1),
      margin: 0,
      [theme.breakpoints.up('sm')]: {
        gridTemplateColumns: '1fr 1fr',
        columnGap: theme.spacing(2),
      },
    },
    field: {
      minWidth: 0,
      margin: 0,
    },
    fieldLabel: {
      color: theme.palette.text.light,
    },
    fieldValue: {
      margin: 0,
      color: theme.palette.primary.dark,
      overflowWrap: 'anywhere',
      '& > div': { marginBottom: theme.spacing(0.5) },
    },
    cardActions: {
      display: 'flex',
      justifyContent: 'flex-end',
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  })
);

const parseEmails = (value?: string): string[] =>
  value ? value.split(',').map((e) => e.trim()).filter(Boolean) : [];

// Backend sends the raw status ("Sent"); unknown values fall back to the raw text.
export const formatStatus = (t: TFunction, status: string) =>
  `${t(`messages:status-${status.trim().toLowerCase()}`, { defaultValue: status })}`;

const formatDate = (datetime?: string) =>
  datetime && isValid(parseISO(String(datetime))) ? format(parseISO(String(datetime)), 'MMM d, yyyy HH:mm') : '';

const MessageCard: FC<{ t: TFunction; message: Message; onOpen: () => void }> = ({ t, message, onOpen }) => {
  const classes = useStyles();
  const fields = [
    { id: 'sender', label: t('messages:sender'), emails: parseEmails(message.sender) },
    { id: 'from', label: t('messages:from'), emails: parseEmails(message.from) },
    { id: 'to', label: t('messages:to'), emails: parseEmails(message.to) },
    { id: 'cc', label: t('messages:cc'), emails: parseEmails(message.cc) },
    { id: 'bcc', label: t('messages:bcc'), emails: parseEmails(message.bcc) },
    { id: 'reply_to', label: t('messages:reply-to'), emails: parseEmails(message.reply_to) },
  ].filter((field) => field.emails.length > 0);

  return (
    <Paper component="article" elevation={0} className={classes.card}>
      <Box className={classes.cardHeader}>
        <Typography variant="caption" component="p">{formatDate(message.datetime)}</Typography>
        {message.status && (
          <Typography variant="caption" component="p" className={classes.status}>
            {`${t('messages:status')}: ${formatStatus(t, message.status)}`}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" component="h3" className={classes.subject}>
        {message.subject}
      </Typography>
      {fields.length > 0 && (
        <Box component="dl" className={classes.fields}>
          {fields.map((field) => (
            <Box key={field.id} className={classes.field}>
              <Typography variant="caption" component="dt" className={classes.fieldLabel}>
                {field.label}
              </Typography>
              <Typography variant="body2" component="dd" className={classes.fieldValue}>
                <EmailsList emails={field.emails} />
              </Typography>
            </Box>
          ))}
        </Box>
      )}
      <Box className={classes.cardActions}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="search"
          text={t('messages:view-message')}
          onClick={onOpen}
        />
      </Box>
    </Paper>
  );
};

const TableMessage: FC<TableMessageProps> = ({ t, messages, setSelectedMessage }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const columns = [
    { id: 'date', label: t('messages:date'), width: '14%', align: 'left' as const },
    { id: 'subject', label: t('messages:subject'), width: '26%', align: 'left' as const },
    { id: 'status', label: t('messages:status'), width: '10%', align: 'left' as const },
    { id: 'sender', label: t('messages:sender'), width: '16%', align: 'left' as const },
    { id: 'recipients', label: t('messages:recipients'), width: '24%', align: 'left' as const },
    { id: 'options', label: t('messages:options'), width: '10%', align: 'center' as const },
  ];

  const renderRecipients = (message: Message) => {
    const to = parseEmails(message.to);
    const cc = parseEmails(message.cc);
    const bcc = parseEmails(message.bcc);
    const recipients = [...to, ...cc, ...bcc];
    if (recipients.length === 0) {
      return <Typography variant="body2">—</Typography>;
    }
    const groups = [
      { label: t('messages:from'), emails: parseEmails(message.from) },
      { label: t('messages:reply-to'), emails: parseEmails(message.reply_to) },
      { label: t('messages:to'), emails: to },
      { label: t('messages:cc'), emails: cc },
      { label: t('messages:bcc'), emails: bcc },
    ].filter((group) => group.emails.length > 0);
    const tooltip = groups.map((group) => `${group.label}: ${group.emails.join(', ')}`).join('\n');
    const remaining = recipients.length - 1;
    const meta = [
      cc.length > 0 ? `${t('messages:cc')} ${cc.length}` : '',
      bcc.length > 0 ? `${t('messages:bcc')} ${bcc.length}` : '',
    ].filter(Boolean).join(' · ');

    return (
      <Box className={classes.recipients} title={tooltip}>
        <Typography variant="body2" className={classes.clampOneLine}>
          {recipients[0]}
          {remaining > 0 ? ` +${remaining}` : ''}
        </Typography>
        {meta && <Typography variant="caption" className={classes.recipientsMeta}>{meta}</Typography>}
      </Box>
    );
  };

  if (!isLg) {
    return (
      <Box className={classes.cardList}>
        {messages.map((message) => (
          <MessageCard
            key={message._id}
            t={t}
            message={message}
            onOpen={() => setSelectedMessage(message)}
          />
        ))}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                align={column.align}
                className={classes.headerCell}
                style={{ width: column.width }}
              >
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {messages.map((message) => (
            <TableRow key={message._id} hover>
              <TableCell align="left" className={classes.cellContainer}>
                <Typography variant="body2" className={classes.clampOneLine} title={formatDate(message.datetime)}>
                  {formatDate(message.datetime)}
                </Typography>
              </TableCell>
              <TableCell align="left" className={classes.cellContainer}>
                <Typography variant="body2" className={classes.clampTwoLines} title={message.subject}>
                  {message.subject}
                </Typography>
              </TableCell>
              <TableCell align="left" className={classes.cellContainer}>
                <Typography variant="body2" className={classes.status}>{message.status ? formatStatus(t, message.status) : ''}</Typography>
              </TableCell>
              <TableCell align="left" className={classes.cellContainer}>
                <Typography variant="body2" className={classes.clampTwoLines} title={message.sender}>
                  {message.sender}
                </Typography>
              </TableCell>
              <TableCell align="left" className={classes.cellContainer}>
                {renderRecipients(message)}
              </TableCell>
              <TableCell align="center" className={classes.cellContainer}>
                <IconButton
                  color="secondary"
                  className={classes.rowAction}
                  aria-label={t('messages:view-message')}
                  onClick={() => setSelectedMessage(message)}
                  size="large"
                >
                  <FontAwesomeIcon icon="search" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableMessage;
